import React, { useEffect, useState } from "react";
import { Button, Form, Input, InputNumber, Modal, Space, Switch, Table, Typography, message } from "antd";
import {
    CreateV2AutomationTrigger,
    DeleteV2Trigger,
    EnsureTriggerExitCondition,
    GetV2AutomationTriggers,
    UpdateV2TriggerEntryEvent,
    UpdateV2Trigger,
    V2TriggerDto
} from "../../api/v2/triggers";
import {TrashIcon} from "../../assets/icons/TrashIcon";
import ConditionEditorModal from "./ConditionsEditorModal";
import ConditionTreeEditorModal from "./ConditionsTreeEditorModal";

const { Text } = Typography;

type Props = {
    domain: string;
    automationId: number;
    onChanged?: () => void;
    eventSuggestions?: string[];
};


export default function TriggersEditor({ domain, automationId, onChanged, eventSuggestions }: Props) {
    const [rows, setRows] = useState<V2TriggerDto[]>([]);
    const [loading, setLoading] = useState(false);

    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [conditionModalOpen, setConditionModalOpen] = useState(false);
    const [editingConditionId, setEditingConditionId] = useState<number | null>(null);
    const [exitModalOpen, setExitModalOpen] = useState(false);
    const [editingExitConditionId, setEditingExitConditionId] = useState<number | null>(null);

    const [form] = Form.useForm();

    const openExitConditionEditor = async (triggerId: number) => {
        const u = await EnsureTriggerExitCondition(domain, triggerId);
        if (!u.ok || !u.data?.exit_condition_id) {
            message.error(u.error || "Failed creating exit condition");
            return;
        }

        setEditingExitConditionId(u.data.exit_condition_id);
        setExitModalOpen(true);
    };

    const reload = async () => {
        setLoading(true);
        try {
            const res = await GetV2AutomationTriggers(domain, automationId);
            if (!res.ok) {
                message.error(res.error || "Failed loading triggers");
                setRows([]);
            } else {
                setRows(res.data || []);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain, automationId]);

    const onCreate = async () => {
        const values = await form.validateFields();
        setCreating(true);
        try {
            const res = await CreateV2AutomationTrigger(domain, automationId, {
                title: values.title,
                cooldown_seconds: values.cooldown_seconds ?? 0,
                max_active_runs_per_client: values.max_active_runs_per_client ?? 1,
                active: values.active ? 1 : 0,
                event_name: values.event_name,
            });

            if (!res.ok) {
                message.error(res.error || "Failed creating trigger");
                return;
            }

            message.success("Trigger created");
            setOpen(false);
            form.resetFields();
            await reload();
            onChanged?.();
        } finally {
            setCreating(false);
        }
    };

    const columns = [
        {
            title: "Active",
            dataIndex: "active",
            width: 60,
            render: (v: any, r: V2TriggerDto) => (
                <Switch
                    checked={!!v}
                    onChange={async (checked) => {
                        const res = await UpdateV2Trigger(domain, r.id, { active: checked ? 1 : 0 });
                        if (!res.ok) message.error(res.error || "Failed updating");
                        else {
                            message.success("Updated");
                            await reload();
                            onChanged?.();
                        }
                    }}
                />
            ),
        },
        {
            title: "Title",
            dataIndex: "title",
            width: 150,
            render: (v: any, r: V2TriggerDto) => (
                <Input
                    defaultValue={v}
                    onBlur={async (e) => {
                        const next = e.target.value?.trim() || "";
                        if (!next || next === v) return;
                        const res = await UpdateV2Trigger(domain, r.id, { title: next });
                        if (!res.ok) message.error(res.error || "Failed updating title");
                        else {
                            message.success("Saved");
                            onChanged?.();
                        }
                    }}
                />
            ),
        },
        {
            title: "Event name",
            dataIndex: "entry_event_name",
            width: 150,
            render: (v: any, r: any) => (
                <Input
                    defaultValue={v || ""}
                    placeholder='e.g. "product.view"'
                    onBlur={async (e) => {
                        const next = e.target.value?.trim() || "";
                        if (!next || next === (v || "")) return;
                        // call new endpoint
                        const res = await UpdateV2TriggerEntryEvent(domain, r.id, { event_name: next });
                        if (!res.ok) message.error(res.error || "Failed updating event name");
                        else {
                            message.success("Saved");
                            await reload();
                            onChanged?.();
                        }
                    }}
                />
            ),
        },
        {
            title: "Entry condition",
            dataIndex: "entry_condition_id",
            width: 80,
            render: (v: any, r: V2TriggerDto) => (
                <Space>
                    <Text>{v}</Text>
                    <Button
                        size="small"
                        onClick={() => {
                            setEditingConditionId(Number(r.entry_condition_id));
                            setConditionModalOpen(true);
                        }}
                    >
                        Edit
                    </Button>
                </Space>
            ),
        },
        {
            title: "Cooldown",
            dataIndex: "cooldown_seconds",
            width: 80,
            render: (v: any, r: V2TriggerDto) => (
                <InputNumber
                    className="v2ae-full-width"
                    min={0}
                    step={60}
                    defaultValue={Number(v || 0)}
                    onBlur={async (e: any) => {
                        const raw = e?.target?.value;
                        const next = Number(raw);
                        if (!Number.isFinite(next) || next === Number(v || 0)) return;
                        const res = await UpdateV2Trigger(domain, r.id, { cooldown_seconds: next });
                        if (!res.ok) message.error(res.error || "Failed updating cooldown");
                        else {
                            message.success("Saved");
                            onChanged?.();
                        }
                    }}
                />
            ),
        },
        {
            title: "Exit condition",
            key: "exit_condition",
            render: (_: any, row: any) => {
                const id = row.exit_condition_id ? Number(row.exit_condition_id) : null;

                return (
                    <Space>
                        <span>{id ? id : "None"}</span>
                        <Button size="small" onClick={() => openExitConditionEditor(Number(row.id))}>
                            {id ? "Edit exit" : "Add exit"}
                        </Button>
                    </Space>
                );
            },
        },
        {
            title: "Max runs/client",
            dataIndex: "max_active_runs_per_client",
            width: 80,
            render: (v: any, r: V2TriggerDto) => (
                <InputNumber
                    className="v2ae-full-width"
                    min={1}
                    step={1}
                    defaultValue={Number(v || 1)}
                    onBlur={async (e: any) => {
                        const raw = e?.target?.value;
                        const next = Number(raw);
                        if (!Number.isFinite(next) || next === Number(v || 1)) return;
                        const res = await UpdateV2Trigger(domain, r.id, { max_active_runs_per_client: next });
                        if (!res.ok) message.error(res.error || "Failed updating max runs");
                        else {
                            message.success("Saved");
                            onChanged?.();
                        }
                    }}
                />
            ),
        },
        // {
        //     title: "Entry condition",
        //     dataIndex: "entry_condition_id",
        //     width: 150,
        //     render: (v: any) => <Text>{v}</Text>,
        // },
        {
            title: "",
            dataIndex: "actions",
            width: 50,
            render: (_: any, r: V2TriggerDto) => (
                <Space>
                    <Button
                        onClick={async () => {
                            const res = await DeleteV2Trigger(domain, r.id);
                            if (!res.ok) message.error(res.error || "Failed deleting");
                            else {
                                message.success("Deleted");
                                await reload();
                                onChanged?.();
                            }
                        }}
                        className="danger-button"
                    >
                        <TrashIcon width={18} />
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Space style={{ marginBottom: 12 }}>
                <Button
                    type="primary"
                    onClick={() => {
                        form.setFieldsValue({
                            title: "New trigger",
                            cooldown_seconds: 0,
                            max_active_runs_per_client: 1,
                            active: true,
                        });
                        setOpen(true);
                    }}
                >
                    Add trigger
                </Button>
                <Button onClick={reload} loading={loading}>
                    Refresh
                </Button>
            </Space>

            <Table<V2TriggerDto>
                rowKey="id"
                loading={loading}
                dataSource={rows}
                columns={columns as any}
                pagination={false}
                size="small"
                className="triggers-table"
            />

            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                onOk={onCreate}
                okButtonProps={{ loading: creating }}
                title="Create trigger"
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="title" label="Title" rules={[{ required: true, message: "Title is required" }]}>
                        <Input />
                    </Form.Item>

                    <Form.Item name="event_name" label="Event name" rules={[{ required: true, message: "Event name is required" }]}>
                        <Input placeholder='e.g. "product.view" or "button.click"' />
                    </Form.Item>

                    <Form.Item name="cooldown_seconds" label="Cooldown (sec)">
                        <InputNumber className="v2ae-full-width" min={0} step={60} />
                    </Form.Item>

                    <Form.Item name="max_active_runs_per_client" label="Max active runs per client">
                        <InputNumber className="v2ae-full-width" min={1} step={1} />
                    </Form.Item>

                    <Form.Item name="active" label="Active" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <Text type="secondary">
                        We created a blank entry condition for this trigger. Next we’ll add the Condition editor to define the event(s) that start it.
                    </Text>
                </Form>
            </Modal>
            {conditionModalOpen && editingConditionId ? (
                <ConditionTreeEditorModal
                    domain={domain}
                    open={conditionModalOpen}
                    rootConditionId={editingConditionId}
                    onClose={() => {
                        setConditionModalOpen(false);
                        setEditingConditionId(null);
                    }}
                    onChanged={async () => {
                        await reload();     // triggers table reload
                        onChanged?.();      // parent refresh trackers
                    }}
                    eventSuggestions={eventSuggestions || []}
                />
            ) : null}
            {exitModalOpen && editingExitConditionId ? (
                <ConditionTreeEditorModal
                    domain={domain}
                    open={exitModalOpen}
                    rootConditionId={editingExitConditionId}
                    onClose={() => {
                        setExitModalOpen(false);
                        setEditingExitConditionId(null);
                    }}
                    onChanged={async () => {
                        await reload(); // refresh triggers + trackers
                    }}
                    eventSuggestions={eventSuggestions || []}
                />
            ) : null}


        </>
    );
}
